import { Entity, MeshInstance } from 'playcanvas';

import { ElementType } from './element';
import { Scene } from './scene';

// duck-typed subset of Model/SceneObject/Water shared by every ElementType.model element
type MeshElement = { name: string, entity: Entity, meshInstances: MeshInstance[] };

// format a number for FBX ASCII output: fixed precision, no exponential notation,
// no trailing zeros
const fmt = (n: number): string => {
    if (!Number.isFinite(n)) return '0';
    return Number(n.toFixed(6)).toString();
};

const fmtArray = (arr: number[]): string => arr.map(fmt).join(',');

// Writes a minimal, valid ASCII FBX 7.3 document covering every model-type scene element
// (imported models, primitive shapes, water - never splats): one Geometry + Model +
// Material per mesh instance, with Vertices/PolygonVertexIndex/LayerElementNormal/
// LayerElementUV blocks and Connections wiring geometry -> model -> material.
// Material scope is diffuse color only (no texture export) - see the plan's Global
// Constraints for why.
const buildFbxExport = (scene: Scene): string => {
    const elements = scene.getElementsByType(ElementType.model) as unknown as MeshElement[];

    if (elements.length === 0) {
        throw new Error('Nothing to export: scene has no model content');
    }

    let nextId = 1000000;
    const nextFbxId = () => nextId++;

    const lines: string[] = [];
    const connections: string[] = [];

    lines.push('; FBX 7.3.0 project file');
    lines.push('FBXHeaderExtension:  {');
    lines.push('    FBXHeaderVersion: 1003');
    lines.push('    FBXVersion: 7300');
    lines.push('    Creator: "Simam 3D Studio"');
    lines.push('}');
    lines.push('GlobalSettings:  {');
    lines.push('    Version: 1000');
    lines.push('    Properties70:  {');
    lines.push('        P: "UpAxis", "int", "Integer", "",1');
    lines.push('        P: "UpAxisSign", "int", "Integer", "",1');
    lines.push('        P: "UnitScaleFactor", "double", "Number", "",1');
    lines.push('    }');
    lines.push('}');
    lines.push('Objects:  {');

    let meshesWritten = 0;

    elements.forEach((element) => {
        element.meshInstances.forEach((meshInstance, meshIndex) => {
            const mesh = meshInstance.mesh;

            const positions: number[] = [];
            mesh.getPositions(positions);
            if (positions.length === 0) return;

            const normals: number[] = [];
            mesh.getNormals(normals);

            const uvs: number[] = [];
            mesh.getUvs(0, uvs);

            let indices: number[] = [];
            mesh.getIndices(indices);
            if (indices.length === 0) {
                // non-indexed mesh: positions are already in triangle order
                indices = Array.from({ length: positions.length / 3 }, (_, i) => i);
            }

            const objName = `${element.name.replace(/\W/g, '_')}_${meshIndex}`;
            const geomId = nextFbxId();
            const modelId = nextFbxId();
            const materialId = nextFbxId();

            // FBX polygon-vertex-index convention: the last index of each polygon is
            // stored as -(index + 1) to mark the polygon boundary. Our meshes are always
            // triangle lists, so every third index gets negated.
            const polyIndices: number[] = [];
            for (let i = 0; i + 2 < indices.length; i += 3) {
                polyIndices.push(indices[i], indices[i + 1], -indices[i + 2] - 1);
            }

            lines.push(`    Geometry: ${geomId}, "Geometry::${objName}", "Mesh" {`);
            lines.push(`        Vertices: *${positions.length} {`);
            lines.push(`            a: ${fmtArray(positions)}`);
            lines.push('        }');
            lines.push(`        PolygonVertexIndex: *${polyIndices.length} {`);
            lines.push(`            a: ${polyIndices.join(',')}`);
            lines.push('        }');

            // getNormals/getUvs return one entry per vertex (control point), matching
            // the position buffer's own layout - so "ByVertice"/"Direct" is the correct
            // (and simplest) mapping, not "ByPolygonVertex".
            if (normals.length === positions.length) {
                lines.push('        LayerElementNormal: 0 {');
                lines.push('            Version: 101');
                lines.push('            Name: ""');
                lines.push('            MappingInformationType: "ByVertice"');
                lines.push('            ReferenceInformationType: "Direct"');
                lines.push(`            Normals: *${normals.length} {`);
                lines.push(`                a: ${fmtArray(normals)}`);
                lines.push('            }');
                lines.push('        }');
            }

            if (uvs.length === (positions.length / 3) * 2) {
                lines.push('        LayerElementUV: 0 {');
                lines.push('            Version: 101');
                lines.push('            Name: ""');
                lines.push('            MappingInformationType: "ByVertice"');
                lines.push('            ReferenceInformationType: "Direct"');
                lines.push(`            UV: *${uvs.length} {`);
                lines.push(`                a: ${fmtArray(uvs)}`);
                lines.push('            }');
                lines.push('        }');
            }

            lines.push('    }');

            const pos = meshInstance.node.getPosition();
            const rot = meshInstance.node.getRotation().getEulerAngles();
            const scl = meshInstance.node.getScale();

            lines.push(`    Model: ${modelId}, "Model::${objName}", "Mesh" {`);
            lines.push('        Version: 232');
            lines.push('        Properties70:  {');
            lines.push(`            P: "Lcl Translation", "Lcl Translation", "", "A",${fmt(pos.x)},${fmt(pos.y)},${fmt(pos.z)}`);
            lines.push(`            P: "Lcl Rotation", "Lcl Rotation", "", "A",${fmt(rot.x)},${fmt(rot.y)},${fmt(rot.z)}`);
            lines.push(`            P: "Lcl Scaling", "Lcl Scaling", "", "A",${fmt(scl.x)},${fmt(scl.y)},${fmt(scl.z)}`);
            lines.push('            P: "RotationOrder", "enum", "", "",0');
            lines.push('        }');
            lines.push('    }');

            const material = meshInstance.material as unknown as { diffuse?: { r: number, g: number, b: number } };
            const diffuse = material?.diffuse ?? { r: 0.8, g: 0.8, b: 0.8 };

            lines.push(`    Material: ${materialId}, "Material::${objName}", "" {`);
            lines.push('        Version: 102');
            lines.push('        ShadingModel: "Lambert"');
            lines.push('        Properties70:  {');
            lines.push(`            P: "DiffuseColor", "Color", "", "A",${fmt(diffuse.r)},${fmt(diffuse.g)},${fmt(diffuse.b)}`);
            lines.push('        }');
            lines.push('    }');

            connections.push(`    C: "OO",${geomId},${modelId}`);
            connections.push(`    C: "OO",${materialId},${modelId}`);
            connections.push(`    C: "OO",${modelId},0`);

            meshesWritten++;
        });
    });

    if (meshesWritten === 0) {
        throw new Error('Nothing to export: no mesh geometry found on scene model content');
    }

    lines.push('}');
    lines.push('Connections:  {');
    lines.push(...connections);
    lines.push('}');

    return lines.join('\n');
};

export { buildFbxExport };
